import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useAtomValue } from 'jotai'
import * as Sentry from '@sentry/electron/renderer'
import { twMerge } from 'tailwind-merge'
import { StagedActionCell, type StagedActionCellProps } from '../../../../integrations/staged-action'
import {
  useStagedActionsQuery,
  useInvokeStagedAction,
  useStagedActionUndo,
  useArchiveStagedActions,
  useStagedActionHooks,
  useStagedActionStatus,
  type StagedAction,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useIsStagedActionEditPending } from '@renderer/overlay/Sidepanel/atoms'
import { Check, X } from 'lucide-react'
import { HighlightAppIcon } from '@renderer/components/Icons/HighlightAppIcon'
import { Button, Cell } from '@highlight/ui'
import { isAnswerableStagedAction, isPendingAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { ResolvedAnswer } from '@renderer/chat/integrations/ask-user-card'
import { isAskUserFeedbackStagedAction, parseAskUserFeedbackInput } from '@renderer/chat/integrations/ask-user-feedback'
import { parseAskUserQuestionInput } from '@renderer/chat/integrations/ask-user-question'
import { ActionViewFeedbackCard } from '@renderer/overlay/Sidepanel/views/action-view/nested/feedback-card'
import { ActionViewQuestionCard } from '@renderer/overlay/Sidepanel/views/action-view/nested/question-card'
import {
  answeredAnswerableOutputs,
  resolvedAnswerableOutputs,
} from '@renderer/overlay/Sidepanel/views/chat-output-view/chat-output'
import { optimisticQuestionAnswersAtom } from '@renderer/overlay/Sidepanel/views/chat-output-view/question-progress-state'

const pendingIcon = (
  <div className="bg-success flex size-3 items-center justify-center rounded-full shadow-sm">
    <Check className="size-2 text-white" strokeWidth={3} />
  </div>
)

const defaultIcon = (
  <div className="bg-foreground text-background flex size-3 shrink-0 items-center justify-center rounded shadow-md">
    <HighlightAppIcon size={8} />
  </div>
)

interface ToolCallView {
  name: string
  icons: Array<string>
  description: string
}

export type StagedActionSnapshot = Pick<StagedAction, 'id'> & Partial<StagedAction>
type RenderableStagedAction = StagedActionCellProps['stagedAction']

function captureStagedActionError(error: unknown, operation: string, actionId: string, extra?: Record<string, string>) {
  Sentry.captureException(error, {
    tags: { area: 'staged-actions-renderer', operation },
    extra: { actionId, ...extra },
  })
}

function toRenderableAction(action: StagedActionSnapshot): RenderableStagedAction {
  return {
    id: action.id,
    actionType: action.actionType ?? 'stagedAction',
    title: action.title ?? action.view?.title ?? 'Action',
    description: action.description ?? '',
    createdAt: action.createdAt,
    toolSet: action.toolSet,
    toolName: action.toolName,
    metadata: action.metadata,
    sourceMetadata: action.sourceMetadata,
    prerequisites: action.prerequisites,
    view: action.view,
  }
}

interface RenderedStagedActionProps {
  action: StagedActionSnapshot
  liveAction: StagedAction | undefined
  toolCallView?: ToolCallView
  onStagedActionClick: (actionId: string, actionType: string) => void
  onInvoke: (actionId: string) => void
  onUndo: (actionId: string) => void
  onDismiss: (actionId: string) => void
  onButtonClick: (actionId: string, button: { link: string }) => void
  onDeleteHook: (actionId: string, trigger: string, effectType: string) => void
  answerablePresentation: 'default' | 'resolved'
}

const RenderedStagedAction = memo(function RenderedStagedAction({
  action,
  liveAction,
  toolCallView,
  onStagedActionClick,
  onInvoke,
  onUndo,
  onDismiss,
  onButtonClick,
  onDeleteHook,
  answerablePresentation,
}: RenderedStagedActionProps) {
  const statusAction = liveAction ?? action
  const renderableAction = toRenderableAction(statusAction)
  const isAnswerable = isAnswerableStagedAction(statusAction)
  const isResolvedAnswerableArtifact = isAnswerable && answerablePresentation === 'resolved'
  const answerableContextTitle = isAnswerable
    ? isAskUserFeedbackStagedAction(statusAction)
      ? parseAskUserFeedbackInput(statusAction.inputData)?.about?.trim()
      : parseAskUserQuestionInput(statusAction.inputData)?.intro?.trim()
    : undefined
  const {
    status: hookStatus,
    errorMessage,
    pendingStartTime,
    isInvoking: isInvokingThisWindow,
    isUndoing,
  } = useStagedActionStatus(statusAction)

  // Reflect the derived invoking *status* (server-reported `lastInvokedAt` with
  // no output), not just this window's invoke flag — an action invoked from
  // another session or still in-flight after a reload must still show the
  // spinner here, not an enabled Invoke button.
  //
  // A draft being rewritten by a scoped-chat edit borrows the same busy
  // treatment: the copy on this card is the pre-edit one, so running it now runs
  // exactly the version the user asked to change.
  const isEditPending = useIsStagedActionEditPending(statusAction.id)
  const isInvoking = hookStatus === 'invoking' || isInvokingThisWindow || isEditPending

  let cellStatus: 'staged' | 'pending' | 'error' | 'undo-error' = 'staged'
  if (hookStatus === 'pending' || hookStatus === 'completed') cellStatus = 'pending'
  else if (hookStatus === 'error') cellStatus = 'error'
  else if (hookStatus === 'undo-error') cellStatus = 'undo-error'

  const supportsUndo = renderableAction.view?.supportsUndo
  const isDismissed = hookStatus === 'archived'
  const isPending = cellStatus === 'pending'
  // Auto-mode's resume_chat hook is implied by the plan card — skip its "|->" row.
  const resumeChatHook = statusAction.hooks?.find(
    (h) => h.trigger === 'execution_finished' && h.effect?.type === 'resume_chat' && h.source !== 'auto-mode-request',
  )
  const showHookLabel = !!resumeChatHook && cellStatus === 'staged' && !isDismissed
  // Same hoisting the output view does: questions the user has already
  // answered (recorded by the floating card's onProgress) settle into compact
  // rows here while the card itself is still pending on a later question.
  const optimisticAnswers = useAtomValue(optimisticQuestionAnswersAtom)
  const answeredQuestions =
    liveAction && isPendingAnswerableStagedAction(liveAction) && optimisticAnswers[liveAction.id]
      ? answeredAnswerableOutputs(liveAction, optimisticAnswers[liveAction.id])
      : []
  const resolvedQuestions =
    liveAction && isResolvedAnswerableArtifact ? (resolvedAnswerableOutputs(liveAction) ?? []) : []
  const displayedQuestions = isResolvedAnswerableArtifact ? resolvedQuestions : answeredQuestions
  const answerableContent =
    liveAction && isAnswerable ? (
      <div className="flex min-w-0 flex-col gap-3">
        {isResolvedAnswerableArtifact && answerableContextTitle && (
          <Cell.Title className="text-foreground line-clamp-none cursor-text select-text text-[13px] font-medium">
            {answerableContextTitle}
          </Cell.Title>
        )}
        {displayedQuestions.map(({ question, answer }) => (
          <div key={question.header} className="flex min-w-0 flex-col gap-1.5">
            {isResolvedAnswerableArtifact ? (
              <Cell.Title
                className={twMerge(
                  'text-foreground cursor-text select-text',
                  answerableContextTitle ? 'font-normal' : 'font-medium',
                )}
              >
                {question.question}
              </Cell.Title>
            ) : (
              <p
                className={twMerge(
                  'text-foreground cursor-text select-text text-[13px] leading-normal',
                  answerableContextTitle ? 'font-normal' : 'font-medium',
                )}
              >
                {question.question}
              </p>
            )}
            <ResolvedAnswer
              answers={answer.selected}
              explanation={answer.explanation}
              emptyLabel={isAskUserFeedbackStagedAction(liveAction) ? 'Skipped' : 'No answer'}
              className={
                isResolvedAnswerableArtifact
                  ? 'border-border w-full rounded-lg border px-2 py-1 shadow-none'
                  : undefined
              }
              iconClassName={isResolvedAnswerableArtifact ? 'text-foreground-placeholder' : undefined}
            />
          </div>
        ))}
        {!isResolvedAnswerableArtifact &&
          (isAskUserFeedbackStagedAction(liveAction) ? (
            <ActionViewFeedbackCard action={liveAction} deferPendingAnswer contentOnly />
          ) : (
            <ActionViewQuestionCard action={liveAction} deferPendingAnswer contentOnly />
          ))}
      </div>
    ) : undefined

  return (
    <div>
      <StagedActionCell
        stagedAction={renderableAction}
        toolCallView={toolCallView}
        status={cellStatus}
        unread
        isInvoking={isInvoking}
        isUndoing={isUndoing}
        isDismissed={isDismissed}
        errorMessage={errorMessage ?? undefined}
        content={answerableContent}
        contentClassName={isResolvedAnswerableArtifact ? 'border-0 bg-transparent p-0' : undefined}
        hideHeader={isResolvedAnswerableArtifact}
        fullContent
        pendingStartTime={pendingStartTime ?? undefined}
        subIcon={isPending ? pendingIcon : defaultIcon}
        onClick={onStagedActionClick}
        onInvoke={isDismissed || isAnswerable ? undefined : onInvoke}
        onUndo={isDismissed ? undefined : supportsUndo ? onUndo : undefined}
        onDismiss={isDismissed ? undefined : onDismiss}
        onButtonClick={isDismissed ? undefined : onButtonClick}
      />
      {showHookLabel && (
        <div className="relative mt-3 flex items-center gap-2 pl-8">
          <div className="absolute left-3 top-0 h-3 w-3.5 rounded-bl-lg border-b border-l" />
          <span className="text-foreground-secondary min-w-0 flex-1 text-[13px]">
            {resumeChatHook.effect.label || 'Continuing…'}
          </span>
          <Button
            type="button"
            aria-label="Remove follow-up"
            onClick={() => onDeleteHook(action.id, resumeChatHook.trigger, resumeChatHook.effect.type)}
            variant="ghost"
            size="xs"
            shape="square"
            className="text-foreground-secondary self-start"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
})

export const StagedActionsRenderer = memo(function StagedActionsRenderer({
  actions,
  onStagedActionClick,
  onStagedActionCreated,
  toolCallView,
  answerablePresentation = 'default',
}: {
  actions: Array<StagedActionSnapshot>
  isLast: boolean
  onStagedActionClick: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
  toolCallView?: ToolCallView
  answerablePresentation?: 'default' | 'resolved'
}) {
  const hasInvalidated = useRef(false)
  const { allActions } = useStagedActionsQuery()
  const invokeStagedActionWithSource = useInvokeStagedAction()
  const { undoStagedAction } = useStagedActionUndo()
  const { dismissStagedAction } = useArchiveStagedActions()
  const { deleteHook } = useStagedActionHooks()
  const liveActionsById = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])
  const visibleActions = useMemo(
    () =>
      answerablePresentation === 'resolved'
        ? actions.filter((action) => {
            const liveAction = liveActionsById.get(action.id)
            return !isAnswerableStagedAction(liveAction ?? action) || liveAction?.outputData != null
          })
        : actions,
    [actions, answerablePresentation, liveActionsById],
  )

  const handleInvoke = useCallback(
    (actionId: string) => {
      invokeStagedActionWithSource(actionId, { source: 'button' }).catch((error) =>
        captureStagedActionError(error, 'invoke', actionId),
      )
    },
    [invokeStagedActionWithSource],
  )

  const handleUndo = useCallback(
    (actionId: string) => {
      undoStagedAction(actionId).catch((error) => captureStagedActionError(error, 'undo', actionId))
    },
    [undoStagedAction],
  )

  const handleDismiss = useCallback(
    (actionId: string) => {
      dismissStagedAction(actionId).catch((error) => captureStagedActionError(error, 'dismiss', actionId))
    },
    [dismissStagedAction],
  )

  const handleButtonClick = useCallback((_actionId: string, button: { link: string }) => {
    window.open(button.link, '_blank')
  }, [])

  const handleDeleteHook = useCallback(
    (actionId: string, trigger: string, effectType: string) => {
      deleteHook(actionId, trigger, effectType).catch((error) =>
        captureStagedActionError(error, 'delete-hook', actionId, { trigger, effectType }),
      )
    },
    [deleteHook],
  )

  useEffect(() => {
    if (!onStagedActionCreated || hasInvalidated.current) return
    hasInvalidated.current = true
    onStagedActionCreated()
  }, [onStagedActionCreated])

  if (visibleActions.length === 0) {
    return null
  }

  return (
    <div className="mb-1 mt-2 flex flex-col gap-2">
      {visibleActions.map((action) => {
        const liveAction = liveActionsById.get(action.id)
        return (
          <RenderedStagedAction
            key={action.id}
            action={action}
            liveAction={liveAction}
            toolCallView={toolCallView}
            onStagedActionClick={onStagedActionClick}
            onInvoke={handleInvoke}
            onUndo={handleUndo}
            onDismiss={handleDismiss}
            onButtonClick={handleButtonClick}
            onDeleteHook={handleDeleteHook}
            answerablePresentation={answerablePresentation}
          />
        )
      })}
    </div>
  )
})
